import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { Chart, type ChartConfiguration } from 'chart.js/auto';

import { DashboardWidgetCard } from '@venecia/molecules';

@Component({
  selector: 'app-dashboard-line-chart-widget',
  imports: [DashboardWidgetCard, TranslateModule],
  templateUrl: './dashboard-line-chart-widget.html',
  styleUrl: './dashboard-line-chart-widget.scss',
})
export class DashboardLineChartWidget implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('canvas')
  set canvasRef(value: ElementRef<HTMLCanvasElement> | undefined) {
    this.canvas = value;

    if (this.viewReady && value) {
      this.initOrUpdateChart();
    }
  }

  @Input({ required: true }) titleKey = '';
  @Input({ required: true }) seriesLabel = '';
  @Input({ required: true }) labels: readonly string[] = [];
  @Input({ required: true }) values: readonly number[] = [];
  @Input() accentRgbVar = 'primary-color-rgb';

  private canvas?: ElementRef<HTMLCanvasElement>;
  private chart: Chart<'line'> | null = null;
  private viewReady = false;

  get hasData(): boolean {
    return this.values.length > 0;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.initOrUpdateChart();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.viewReady) {
      return;
    }

    this.initOrUpdateChart();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private initOrUpdateChart(): void {
    const labels = [...this.labels];
    const values = [...this.values];

    if (values.length === 0) {
      this.destroyChart();
      return;
    }

    const context = this.canvas?.nativeElement.getContext('2d');
    if (!context) {
      this.destroyChart();
      return;
    }

    if (!this.chart) {
      this.chart = new Chart(context, this.buildConfiguration(labels, values));
      return;
    }

    this.chart.data.labels = labels;
    this.chart.data.datasets[0].data = values;
    this.chart.data.datasets[0].label = this.seriesLabel;
    this.chart.update();
  }

  private buildConfiguration(labels: readonly string[], values: readonly number[]): ChartConfiguration<'line'> {
    const accentRgb = this.cssVar(this.accentRgbVar, '111, 77, 214');
    const textSoft = this.cssVar('text-soft', '#7a7a85');
    const borderColor = `rgba(${accentRgb}, 0.2)`;
    const lineColor = `rgba(${accentRgb}, 0.95)`;
    const areaColor = `rgba(${accentRgb}, 0.22)`;

    return {
      type: 'line',
      data: {
        labels: [...labels],
        datasets: [
          {
            backgroundColor: areaColor,
            borderColor: lineColor,
            borderWidth: 2,
            data: [...values],
            fill: true,
            label: this.seriesLabel,
            pointBackgroundColor: lineColor,
            pointBorderColor: lineColor,
            pointHoverRadius: 3,
            pointRadius: 2,
            tension: 0.35,
          },
        ],
      },
      options: {
        animation: false,
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
          },
        },
        scales: {
          x: {
            grid: {
              color: borderColor,
            },
            ticks: {
              autoSkip: true,
              color: textSoft,
              maxRotation: 0,
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: borderColor,
            },
            ticks: {
              color: textSoft,
            },
          },
        },
      },
    };
  }

  private destroyChart(): void {
    this.chart?.destroy();
    this.chart = null;
  }

  private cssVar(name: string, fallback: string): string {
    if (typeof document === 'undefined') {
      return fallback;
    }

    const value = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
    return value || fallback;
  }
}
